import React, { useCallback, useEffect, useRef } from 'react';
import Icon from './Icon';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';

/**
 * Full-screen image viewer for the showroom gallery and the catalogue.
 *
 * Handles Escape, arrow-key paging, a focus trap and a restore of focus to
 * whatever opened it.
 */
export function Lightbox({ items, index, onClose, onNavigate }) {
  const open = index !== null && index >= 0;
  const panelRef = useRef(null);
  const openerRef = useRef(null);

  useLockBodyScroll(open);

  // Remember what had focus so it can be handed back on close.
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
    else if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open, index]);

  const step = useCallback(
    (delta) => {
      if (!open) return;
      onNavigate((index + delta + items.length) % items.length);
    },
    [open, index, items.length, onNavigate]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'Tab') {
        // Only the dialog's own controls are reachable while it is open.
        const focusables = panelRef.current?.querySelectorAll('button');
        if (!focusables?.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, step]);

  if (!open) return null;

  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-950/95 p-4 animate-fade-in backdrop-blur-sm sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={item.alt || item.name || 'Image viewer'}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={panelRef} tabIndex={-1} className="relative flex max-h-full w-full max-w-6xl flex-col items-center gap-5 outline-none">
        <div className="relative flex w-full items-center justify-center">
          <img
            key={item.src}
            src={item.src}
            alt={item.alt || item.name || ''}
            className="max-h-[74vh] w-auto max-w-full rounded-sm object-contain shadow-lift animate-fade-in"
          />
        </div>

        <div className="flex w-full items-center justify-between gap-4 text-canvas">
          <div className="min-w-0">
            {item.name && <p className="truncate font-display text-xl">{item.name}</p>}
            {(item.note || item.alt) && (
              <p className="truncate text-xs uppercase tracking-widest text-canvas/50">
                {item.note || item.alt}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span className="mr-2 font-sans text-xs tabular-nums tracking-widest text-canvas/50">
              {String(index + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
            </span>
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous image"
              className="grid h-11 w-11 place-items-center rounded-full border border-canvas/20 text-canvas transition hover:border-champagne-300 hover:text-champagne-300"
            >
              <Icon name="chevronLeft" />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next image"
              className="grid h-11 w-11 place-items-center rounded-full border border-canvas/20 text-canvas transition hover:border-champagne-300 hover:text-champagne-300"
            >
              <Icon name="chevronRight" />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close viewer"
              className="grid h-11 w-11 place-items-center rounded-full bg-canvas text-ink-950 transition hover:bg-champagne-300"
            >
              <Icon name="close" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Lightbox;
